import React, { useState, useEffect } from "react";
import axios from "axios";
import { Play, ShoppingBag, FileText } from "lucide-react";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { API, BACKEND_URL } from "../config";
import { formatHUF, getDisplayPrice } from "../lib/format";
import Footer from "./components/Footer";
import { useMusicPlayer } from "../contexts/AppContext";
import { useCart } from "../contexts/AppContext";
import { useSettings } from "../contexts/AppContext";
import { useFanClub } from "../contexts/AppContext";

export default function MusicPage() {
  const [albums, setAlbums] = useState([]);
  const [selectedAlbum, setSelectedAlbum] = useState(null);
  const { playTrack, currentTrack, isPlaying, toggleLyrics } = useMusicPlayer();
  const { addToCart } = useCart();
  const { shop_enabled } = useSettings();
  const { member, tiers } = useFanClub();

  useEffect(() => {
    axios.get(`${API}/albums`).then(res => { setAlbums(res.data); if (res.data.length > 0) setSelectedAlbum(res.data[0]); }).catch(console.error);
  }, []);

  return (
    <>
    <div className="min-h-screen pt-24 sm:pt-32 pb-32 px-4 sm:px-6 md:px-12 lg:px-24" data-testid="music-page">
      <div className="max-w-7xl mx-auto">
        <div className="mb-8 sm:mb-16 animate-fade-in">
          <p className="text-[10px] sm:text-xs uppercase tracking-[0.2em] text-primary mb-2 sm:mb-4">Diszkográfia</p>
          <h1 className="text-3xl sm:text-5xl md:text-7xl font-bold font-['Syne'] tracking-tighter">Zene</h1>
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 sm:gap-12">
          <div className="lg:col-span-5 space-y-4">
            <h2 className="text-lg sm:text-xl font-bold font-['Syne'] mb-2">Albumok</h2>
            <div className="grid grid-cols-3 sm:grid-cols-2 gap-2 sm:gap-4">
              {albums.map((album) => (
                <button key={album.id} onClick={() => setSelectedAlbum(album)}
                  className={`relative rounded-lg sm:rounded-xl overflow-hidden group transition-all ${selectedAlbum?.id === album.id ? 'ring-2 ring-primary' : ''}`}>
                  <div className="aspect-square"><img src={`${BACKEND_URL}/storage/${album.cover_image}`} alt={album.title} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110" /></div>
                  <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center"><Play className="h-6 w-6 sm:h-10 sm:w-10" /></div>
                  <div className="absolute bottom-0 left-0 right-0 p-2 sm:p-3 bg-gradient-to-t from-black to-transparent">
                    <p className="font-bold text-[10px] sm:text-sm truncate">{album.title}</p>
                    <p className="text-[9px] sm:text-xs text-muted-foreground">{album.release_year}</p>
                  </div>
                </button>
              ))}
            </div>
          </div>

          {selectedAlbum && (
            <div className="lg:col-span-7 animate-fade-in">
              <div className="glass rounded-xl sm:rounded-2xl p-4 sm:p-6 md:p-8">
                <div className="flex flex-col sm:flex-row gap-4 sm:gap-8 mb-6">
                  <div className="w-full sm:w-48 md:w-64 shrink-0">
                    <img src={`${BACKEND_URL}/storage/${selectedAlbum.cover_image}`} alt={selectedAlbum.title} className="w-full aspect-square object-cover rounded-lg sm:rounded-xl shadow-2xl" />
                  </div>
                  <div className="flex-1">
                    <Badge className="bg-primary/20 text-primary mb-2 sm:mb-4 text-[10px] sm:text-xs">{selectedAlbum.release_year}</Badge>
                    <h2 className="text-xl sm:text-3xl font-bold font-['Syne'] mb-2 sm:mb-4">{selectedAlbum.title}</h2>
                    <p className="text-muted-foreground text-xs sm:text-base mb-4">{selectedAlbum.description}</p>
                    <div className="flex flex-wrap items-center gap-2 sm:gap-4">
                      <Button onClick={() => playTrack(selectedAlbum.tracks[0], selectedAlbum, selectedAlbum.tracks)} className="rounded-full px-4 sm:px-8 text-xs sm:text-sm bg-primary hover:bg-primary/90">
                        <Play className="h-3 w-3 sm:h-4 sm:w-4 mr-1 sm:mr-2" />Lejátszás
                      </Button>
                      {shop_enabled && (
                        <Button variant="outline" onClick={() => addToCart({ product_id: selectedAlbum.id, product_name: `${selectedAlbum.title} (Digitális)`, price: selectedAlbum.price, image: selectedAlbum.cover_image, item_type: 'album' })}
                          className="rounded-full px-4 sm:px-8 text-xs sm:text-sm border-white/20">
                          <ShoppingBag className="h-3 w-3 sm:h-4 sm:w-4 mr-1 sm:mr-2" />{formatHUF(getDisplayPrice(selectedAlbum.price, member, tiers))}
                        </Button>
                      )}
                    </div>
                  </div>
                </div>

                <div className="space-y-1">
                  <h3 className="text-base sm:text-lg font-bold font-['Syne'] mb-2 sm:mb-4">Számlista</h3>
                  {selectedAlbum.tracks.map((track, index) => (
                    <div key={track.id} className={`flex items-center gap-2 sm:gap-4 p-2 sm:p-4 rounded-lg transition-colors ${currentTrack?.id === track.id ? 'bg-primary/20' : 'hover:bg-white/5'}`}>
                      <button onClick={() => playTrack(track, selectedAlbum, selectedAlbum.tracks)} className="w-6 sm:w-8 text-muted-foreground text-xs sm:text-sm">
                        {currentTrack?.id === track.id && isPlaying ? (
                          <div className="flex gap-0.5 items-end justify-center h-4">
                            <div className="w-1 bg-primary animate-pulse" style={{ height: '60%' }} />
                            <div className="w-1 bg-primary animate-pulse" style={{ height: '100%', animationDelay: '0.2s' }} />
                            <div className="w-1 bg-primary animate-pulse" style={{ height: '40%', animationDelay: '0.4s' }} />
                          </div>
                        ) : String(index + 1).padStart(2, '0')}
                      </button>
                      <button onClick={() => playTrack(track, selectedAlbum, selectedAlbum.tracks)} className="flex-1 text-left font-medium text-xs sm:text-base">{track.title}</button>
                      {track.lyrics && (
                        <Button variant="ghost" size="icon" className="h-7 w-7 hidden sm:flex" onClick={() => { playTrack(track, selectedAlbum, selectedAlbum.tracks); toggleLyrics(); }}>
                          <FileText className="h-3 w-3" />
                        </Button>
                      )}
                      <span className="text-muted-foreground text-xs sm:text-sm">{track.duration}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
    <Footer />
    </>
  );
}
